import React, { useState } from 'react';
import { Mail, Send } from 'lucide-react';

function ContactUs() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, topic, message }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('Message sent successfully! We\'ll get back to you within 24 hours.');
        setName('');
        setEmail('');
        setTopic('');
        setMessage('');
      } else {
        setSubmitError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '60px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: '48px',
            fontWeight: '800',
            margin: '0 0 20px 0',
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Get In Touch
          </h1>
          <p style={{ 
            fontSize: '16px',
            color: '#64748b',
            margin: 0
          }}>
            Have questions, feedback, or suggestions? We'd love to hear from you.
          </p>
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid rgba(0, 217, 255, 0.3)',
          borderLeft: '4px solid #00d9ff',
          borderRadius: '12px',
          padding: '25px',
          marginBottom: '40px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <Mail size={20} color="#00d9ff" />
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#ffffff' }}>
              Direct Line to the Founder
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: '14px', color: '#a0aec0', lineHeight: '1.6' }}>
            Every message is sent directly to me, and I personally respond to each one within 24 hours. 
            Your feedback helps shape the future of our platform.
          </p>
        </div>

        {/* Form */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '40px'
        }}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px',
                fontWeight: '600',
                color: '#a0aec0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Your Name
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  fontSize: '15px',
                  backgroundColor: '#0f1419',
                  color: '#ffffff',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                onBlur={(e) => e.target.style.borderColor = '#2d3748'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px',
                fontWeight: '600',
                color: '#a0aec0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  fontSize: '15px',
                  backgroundColor: '#0f1419',
                  color: '#ffffff',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                onBlur={(e) => e.target.style.borderColor = '#2d3748'}
              />
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px',
                fontWeight: '600',
                color: '#a0aec0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                What can we help you with?
              </label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  fontSize: '15px',
                  backgroundColor: '#0f1419',
                  color: topic ? '#ffffff' : '#64748b',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                onBlur={(e) => e.target.style.borderColor = '#2d3748'}
              >
                <option value="">Select a topic</option>
                <option value="general">General Inquiry</option>
                <option value="technical">Technical Support</option>
                <option value="billing">Billing Question</option>
                <option value="feature">Feature Request</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px',
                fontWeight: '600',
                color: '#a0aec0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Message
              </label>
              <textarea
                placeholder="Tell us what's on your mind..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                maxLength={2000}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  fontSize: '15px',
                  minHeight: '180px',
                  resize: 'vertical',
                  backgroundColor: '#0f1419',
                  color: '#ffffff',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                onBlur={(e) => e.target.style.borderColor = '#2d3748'}
              />
              <div style={{ 
                textAlign: 'right', 
                fontSize: '12px', 
                marginTop: '6px', 
                color: '#64748b'
              }}>
                {message.length} / 2000 characters
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                background: isSubmitting ? '#4a5568' : 'linear-gradient(135deg, #00d9ff 0%, #10b981 100%)',
                color: '#0a0e1a',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0, 217, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
              onMouseOver={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0, 217, 255, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 217, 255, 0.3)';
                }
              }}
            >
              <Send size={18} />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {/* Success Message */}
            {submitMessage && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                color: '#10b981',
                fontSize: '14px'
              }}>
                {submitMessage}
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px'
              }}>
                {submitError}
              </div>
            )}
          </form>

          {/* Prefer Email Section */}
          <div style={{
            marginTop: '40px',
            paddingTop: '30px',
            borderTop: '1px solid #2d3748',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '14px', 
              marginBottom: '10px', 
              color: '#a0aec0'
            }}>
              Prefer Email?
            </p>
            <p style={{ fontSize: '14px', margin: 0 }}>
              Reach us at{' '}
              <a 
                href="mailto:support@upperlinebet.com" 
                style={{ 
                  color: '#00d9ff', 
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                support@upperlinebet.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;